import { Modal, SubmitButton } from "@xola/ui-kit";
import { getItemLineItemsByType, handleSuccessfulSubmission } from "common";
import { sumBy } from "lodash";
import React, { useMemo, useState } from "react";
import { useMutation, useQuery } from "react-query";
import { LineItemType, Purchase } from "types";
import { api } from "@/api/api";
import { Currency } from "@/components/Common/Currency";
import { RadioInput } from "@/components/Common/RadioInput";
import { SecondaryButton } from "@/components/Common/SecondaryButton";
import { useInvalidate } from "@/hooks/useInvalidation";

interface SplitPackageModalProps {
    isOpen: boolean;
    purchase: Purchase;
    onClose: (newPurchase?: Purchase) => void;
}

export const SplitPackageModal = ({ isOpen, purchase, onClose }: SplitPackageModalProps) => {
    return (
        <Modal isOpen={isOpen} onClose={() => onClose()}>
            <ModalContent purchase={purchase} onClose={onClose} />
        </Modal>
    );
};

const ModalContent = ({ purchase, onClose }: Omit<SplitPackageModalProps, "isOpen">) => {
    const invalidate = useInvalidate();
    const [retainDiscount, setRetainDiscount] = useState(true);
    const splitItems = useMemo(
        () => purchase.items.map((item) => ({ id: item.id, parentProduct: null })),
        [purchase.items],
    );

    const discountAmount = useMemo(() => {
        const packageDiscounts = purchase.items.flatMap(
            (item) => getItemLineItemsByType(item, LineItemType.PACKAGE_DISCOUNT) ?? [],
        );
        return Math.abs(sumBy(packageDiscounts, "amount"));
    }, [purchase.items]);
    const hasDiscount = discountAmount > 0;

    const { data: previewBalance, isLoading: isPreviewLoading } = useQuery(
        ["splitPackage", "preview", purchase.id],
        async () => {
            const { data } = await api.patch<Purchase>(`/purchases/${purchase.id}?dryRun=true`, {
                items: splitItems,
                _retainDiscount: false,
            });
            return data.balance;
        },
        { enabled: hasDiscount },
    );

    const {
        mutateAsync: confirmSplit,
        isLoading: isSplitting,
        isSuccess,
    } = useMutation(async () => {
        const { data } = await api.patch<Purchase>(`/purchases/${purchase.id}`, {
            items: splitItems,
            _retainDiscount: retainDiscount,
            _emailOverrides: { sendEmailToTraveler: true },
        });
        invalidate("purchases", purchase.id);
        handleSuccessfulSubmission(() => onClose(data));
    });

    const balanceForRemove = useMemo(
        () => (previewBalance ?? 0) - purchase.balance,
        [previewBalance, purchase.balance],
    );

    return (
        <>
            <Modal.Header>Split Package?</Modal.Header>
            <Modal.Body>
                <div className="space-y-4">
                    <p>
                        You are about to split this package into a purchase with individual reservations.
                        <span className="ml-0.5 font-bold">This action cannot be undone.</span>
                    </p>

                    {hasDiscount && (
                        <>
                            <p>
                                This purchase currently has a
                                <span className="mx-0.5 font-bold">
                                    <Currency>{discountAmount}</Currency>
                                </span>
                                package discount. Would you like to retain or remove the package discount?
                            </p>
                            <div className="space-y-2">
                                <RadioInput
                                    name="retainDiscount"
                                    checked={retainDiscount}
                                    onChange={() => setRetainDiscount(true)}
                                >
                                    Retain package discount
                                </RadioInput>
                                <RadioInput
                                    name="retainDiscount"
                                    checked={!retainDiscount}
                                    onChange={() => setRetainDiscount(false)}
                                >
                                    Remove package discount
                                    {balanceForRemove > 0 && (
                                        <span className="ml-0.5 text-gray-darker">
                                            (results in <Currency>{balanceForRemove}</Currency> balance due)
                                        </span>
                                    )}
                                </RadioInput>
                            </div>
                        </>
                    )}
                </div>
            </Modal.Body>

            <Modal.Footer className="flex items-center justify-end space-x-4">
                <SecondaryButton disabled={isSplitting} onClick={() => onClose()}>
                    Cancel
                </SecondaryButton>
                <SubmitButton
                    disabled={hasDiscount && isPreviewLoading}
                    isLoading={isSplitting}
                    isSuccess={isSuccess}
                    onClick={confirmSplit}
                >
                    Confirm and Split Package
                </SubmitButton>
            </Modal.Footer>
        </>
    );
};
